import React, { useState, useEffect, useRef } from 'react';
import config from '../config';
import '../styles/GiphySearchBox.css';

function GiphySearchBox({ onGifSelect, onClose }) {
  const [apiKey, setApiKey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [gifs, setGifs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const searchBoxRef = useRef(null);

  useEffect(() => {
    // Check if API key is configured
    const key = config.giphyApiKey;
    if (key && key !== 'your_giphy_api_key_here') {
      setApiKey(key);
      // Load trending GIFs initially
      loadTrendingGifs(key);
    }
    setIsLoading(false);
  }, []);

  const loadTrendingGifs = async (key) => {
    try {
      setIsSearching(true);
      const response = await fetch(`https://api.giphy.com/v1/gifs/trending?api_key=${key}&limit=20`);
      const data = await response.json();
      
      if (data.data) {
        setGifs(data.data);
      }
    } catch (error) {
      console.error('Error loading trending GIFs:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const searchGifs = async (query) => {
    if (!apiKey || !query.trim()) {
      loadTrendingGifs(apiKey);
      return;
    }

    try {
      setIsSearching(true);
      const response = await fetch(`https://api.giphy.com/v1/gifs/search?api_key=${apiKey}&q=${encodeURIComponent(query)}&limit=20`);
      const data = await response.json();
      
      if (data.data) {
        setGifs(data.data);
      }
    } catch (error) {
      console.error('Error searching GIFs:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Debounce search
    clearTimeout(searchBoxRef.current.searchTimeout);
    searchBoxRef.current.searchTimeout = setTimeout(() => {
      searchGifs(value);
    }, 500);
  };

  const handleGifClick = (gif) => {
    if (onGifSelect) {
      // Prefer smaller variants so the chat loads the GIF faster
      const gifUrl =
        gif.images?.fixed_height?.url ||
        gif.images?.downsized?.url ||
        gif.images?.downsized_medium?.url ||
        gif.images?.original?.url;
      const gifName = gif.title || 'GIF';
      onGifSelect(gifUrl, gifName);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="giphy-search-box" ref={searchBoxRef}>
        <div className="giphy-loading">
          <div className="loading-spinner"></div>
          <p>Loading GIF search...</p>
        </div>
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div className="giphy-search-box" ref={searchBoxRef}>
        <div className="giphy-error">
          <div className="error-icon">🔑</div>
          <h3>Giphy API Key Required</h3>
          <p>Please set your Giphy API key in the environment variables.</p>
          <p>Get your free API key from: <a href="https://developers.giphy.com" target="_blank" rel="noopener noreferrer">developers.giphy.com</a></p>
          <button className="close-btn" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="giphy-search-box" ref={searchBoxRef}>
      <div className="giphy-header">
        <h3>Search GIFs</h3>
        <button className="close-btn" onClick={handleClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
      
      <div className="giphy-content">
        {/* Search Input */}
        <div className="giphy-search-input-container">
          <input
            type="text"
            placeholder="Search GIFs..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="giphy-search-input"
          />
          {isSearching && <div className="search-spinner"></div>}
        </div>

        {/* GIF Grid */}
        <div className="giphy-grid">
          {gifs.map((gif) => (
            <div
              key={gif.id}
              className="giphy-item"
              onClick={() => handleGifClick(gif)}
            >
              <img
                src={gif.images?.fixed_height?.url || gif.images?.downsized?.url}
                alt={gif.title || 'GIF'}
                className="giphy-image"
                loading="lazy"
              />
              <div className="giphy-overlay">
                <div className="giphy-title">{gif.title || 'GIF'}</div>
              </div>
            </div>
          ))}
        </div>

        {gifs.length === 0 && !isSearching && (
          <div className="no-gifs">
            <p>No GIFs found</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default GiphySearchBox;

